<script>
  // The media kit: what visitors can download from /media (linked from the footer). Add files, edit their title, description and section, remove them.
  // Edits save themselves; removing a file also removes it from storage.
  import { onMount } from 'svelte';
  import Button from '../ui/Button.svelte';
  import Card from '../ui/Card.svelte';
  import Sheet from '../ui/Sheet.svelte';
  import { inputCls, textareaCls, selectCls, labelCls, hintCls } from '../../lib/ui.js';
  import { toast } from '../../lib/toast.js';
  import { confirmDialog } from '../../lib/confirm.js';
  import Plus from 'lucide-svelte/icons/plus';
  import Trash2 from 'lucide-svelte/icons/trash-2';
  import Pencil from 'lucide-svelte/icons/pencil';
  import ExternalLink from 'lucide-svelte/icons/external-link';
  import LoaderCircle from 'lucide-svelte/icons/loader-circle';
  import FileText from 'lucide-svelte/icons/file-text';

  let assets = $state([]);
  let groups = $state([]);
  let storage = $state('cloudinary');
  let loading = $state(true);
  let uploading = $state(false);
  let group = $state('Logos');                   // the section new uploads go into
  let input;

  async function api(method, url, body) {
    const res = await fetch(url, { method, credentials: 'include', headers: body && !(body instanceof FormData) ? { 'Content-Type': 'application/json' } : undefined, body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
  }
  onMount(async () => {
    try { const r = await api('GET', '/api/admin/media-assets'); assets = r.assets; groups = r.groups; storage = r.storage; }
    catch (e) { toast.error(e.message); }
    finally { loading = false; }
  });

  async function upload(files) {
    if (!files?.length || uploading) return;
    uploading = true;
    try {
      const fd = new FormData();
      fd.append('group', group);
      for (const f of files) fd.append('files', f);
      const r = await api('POST', '/api/admin/media-assets', fd);
      assets = [...assets, ...r.assets];
      toast.success(`${r.assets.length} file${r.assets.length === 1 ? '' : 's'} added.`);
    } catch (e) { toast.error(e.message); }
    finally { uploading = false; if (input) input.value = ''; }
  }

  const isImage = (a) => /^image\/(svg\+xml|png|jpeg|webp|gif)$/.test(a.mime);
  const onDark = (a) => /white|light|reverse/i.test(a.title);
  const size = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : b ? `${Math.max(1, Math.round(b / 1024))} KB` : '');
  const sections = $derived([...groups, ...new Set(assets.map(a => a.group).filter(g => !groups.includes(g)))].map(g => ({ name: g, items: assets.filter(a => a.group === g) })).filter(s => s.items.length));

  // The editor saves itself shortly after you stop typing.
  let editing = $state(null);
  let saved = $state('');
  let state = $state('');
  let open = $state(false);
  const fields = (a) => JSON.stringify({ title: a.title, description: a.description, group: a.group });
  function edit(a) { editing = { ...a }; saved = fields(a); state = ''; open = true; }
  $effect(() => {
    if (!open || !editing) return;
    const snap = fields(editing);
    if (snap === saved) return;
    const t = setTimeout(() => save(snap), 600);
    return () => clearTimeout(t);
  });
  async function save(snap) {
    const now = JSON.parse(snap);
    if (!now.title.trim()) return;
    state = 'saving';
    try { const a = await api('PATCH', `/api/admin/media-assets/${editing.id}`, now); assets = assets.map(x => (x.id === a.id ? a : x)); saved = snap; state = 'saved'; }
    catch (e) { toast.error(e.message); state = 'error'; }
  }
  async function remove(a) {
    if (!(await confirmDialog.ask({ title: `Remove “${a.title}”?`, description: 'It disappears from the public Media page and the file is deleted. This cannot be undone.', confirmLabel: 'Remove', destructive: true }))) return;
    try { await api('DELETE', `/api/admin/media-assets/${a.id}`); assets = assets.filter(x => x.id !== a.id); open = false; }
    catch (e) { toast.error(e.message); }
  }
</script>

<div class="space-y-6">
  <div class="flex flex-wrap items-end justify-between gap-4">
    <div>
      <h1 class="text-2xl font-semibold tracking-tight">Media kit</h1>
      <p class="text-sm text-muted-foreground">Logos, photos and press files anyone can download from your Media page. The page is linked from the footer only.</p>
    </div>
    <div class="flex items-center gap-2">
      <a href="/media" target="_blank" rel="noopener" class="inline-flex h-9 items-center gap-2 rounded-md border border-input bg-background px-4 text-sm font-medium shadow-xs hover:bg-accent"><ExternalLink size={15} /> View page</a>
    </div>
  </div>

  <Card>
    <div class="flex flex-wrap items-end gap-3 p-4 md:p-6">
      <div class="w-48"><label class={labelCls} for="mk-group">Add to</label>
        <select id="mk-group" class={selectCls} bind:value={group}>{#each groups as g}<option value={g}>{g}</option>{/each}</select></div>
      <input bind:this={input} type="file" multiple accept=".svg,.png,.jpg,.jpeg,.webp,.gif,.pdf,.zip,image/svg+xml,image/png,image/jpeg,image/webp,image/gif,application/pdf,application/zip" class="sr-only" id="mk-files" onchange={(e) => upload([...e.currentTarget.files])} />
      <Button onclick={() => input.click()} disabled={uploading || storage === 'none'}>{#if uploading}<LoaderCircle size={14} class="animate-spin" />{:else}<Plus size={16} />{/if} Add files</Button>
      <p class="basis-full text-xs text-muted-foreground">SVG, PNG, JPG, WebP, GIF, PDF or ZIP, up to 25 MB each.{#if storage === 'none'} <span class="text-destructive">File storage is not set up on this server, so files cannot be added.</span>{/if}</p>
    </div>
  </Card>

  {#if loading}
    <div class="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground" role="status"><LoaderCircle size={16} class="animate-spin" /> Loading…</div>
  {:else if sections.length === 0}
    <Card><div class="py-14 text-center"><p class="font-medium">Nothing in the media kit</p><p class="text-sm text-muted-foreground">Add files above and they appear on your Media page.</p></div></Card>
  {:else}
    {#each sections as s (s.name)}
      <section aria-label={s.name}>
        <h2 class="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{s.name}</h2>
        <ul class="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {#each s.items as a (a.id)}
            <li class="overflow-hidden rounded-xl border border-border bg-card">
              <div class="relative grid aspect-[4/3] place-items-center p-5 {onDark(a) ? 'bg-neutral-900' : 'bg-neutral-200'}">
                {#if isImage(a)}<img src={a.url} alt="" class="max-h-full max-w-full object-contain" loading="lazy" />{:else}<FileText size={28} class="text-muted-foreground" />{/if}
              </div>
              <div class="flex items-start gap-2 p-3">
                <div class="min-w-0 flex-1"><p class="truncate text-sm font-medium">{a.title}</p><p class="text-xs text-muted-foreground">{(a.ext || '').toUpperCase()}{size(a.bytes) ? ` · ${size(a.bytes)}` : ''}</p></div>
                <Button variant="ghost" size="icon" aria-label="Edit {a.title}" onclick={() => edit(a)}><Pencil size={15} /></Button>
                <Button variant="ghost" size="icon" aria-label="Remove {a.title}" class="hover:text-destructive" onclick={() => remove(a)}><Trash2 size={15} /></Button>
              </div>
            </li>
          {/each}
        </ul>
      </section>
    {/each}
  {/if}
</div>

<Sheet bind:open title="Edit file" description="Changes save automatically.">
  {#if editing}
    <div class="space-y-5">
      <div><label class={labelCls} for="me-title">Title</label><input id="me-title" class={inputCls} bind:value={editing.title} maxlength="80" /></div>
      <div><label class={labelCls} for="me-desc">Description <span class="font-normal text-muted-foreground">(optional)</span></label><textarea id="me-desc" class={textareaCls} bind:value={editing.description} maxlength="300" rows="3" placeholder="Where and how to use it"></textarea></div>
      <div><label class={labelCls} for="me-group">Section</label><select id="me-group" class={selectCls} bind:value={editing.group}>{#each groups as g}<option value={g}>{g}</option>{/each}</select>
        <p class={hintCls}>A title with “white” in it is shown on a dark card, so white logos are visible.</p></div>
      <div class="flex items-center justify-between pt-2">
        <Button variant="ghost" class="text-destructive hover:text-destructive" onclick={() => remove(editing)}><Trash2 size={14} /> Remove</Button>
        <span class="text-xs {state === 'error' ? 'text-destructive' : 'text-muted-foreground'}" role="status" aria-live="polite">{state === 'saving' ? 'Saving…' : state === 'saved' ? 'Saved' : state === 'error' ? 'Not saved' : 'Changes save automatically'}</span>
      </div>
    </div>
  {/if}
</Sheet>
